import { useEffect, useMemo, useState } from "react";
import { Plus, Trash2, StickyNote } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import ExportButton from "@/components/ExportButton";
import { api } from "@/lib/api";
import { formatDateEU } from "@/lib/constants";

const PARENT_COLORS = {
  lead: "#457B9D",
  customer: "#2A9D8F",
  sample: "#7B6DCC",
  task: "#E85D04",
};

export default function Notes() {
  const [notes, setNotes] = useState([]);
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ content: "", parent_type: "lead", parent_id: null, parent_label: "" });
  const [leads, setLeads] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [samples, setSamples] = useState([]);
  const [tasks, setTasks] = useState([]);

  const load = async () => {
    const [n, l, c, s, t] = await Promise.all([
      api.listNotes(), api.listLeads(), api.listCustomers(), api.listSamples(), api.listTasks(),
    ]);
    setNotes(n);
    setLeads(l); setCustomers(c); setSamples(s); setTasks(t);
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(
    () => (filter === "all" ? notes : notes.filter((n) => n.parent_type === filter)),
    [notes, filter]
  );

  const parentOptions = useMemo(() => {
    switch (form.parent_type) {
      case "lead": return leads.map((x) => ({ id: x.id, label: x.company_name }));
      case "customer": return customers.map((x) => ({ id: x.id, label: x.customer_name }));
      case "sample": return samples.map((x) => ({ id: x.id, label: `${x.company_name} · ${formatDateEU(x.date_delivered)}` }));
      case "task": return tasks.map((x) => ({ id: x.id, label: x.task_name }));
      default: return [];
    }
  }, [form.parent_type, leads, customers, samples, tasks]);

  const openCreate = () => {
    setForm({ content: "", parent_type: "lead", parent_id: null, parent_label: "" });
    setOpen(true);
  };

  const save = async () => {
    if (!form.content.trim()) return toast.error("Note content is required");
    try {
      await api.createNote(form);
      toast.success("Note added");
      setOpen(false);
      load();
    } catch { toast.error("Failed to add note"); }
  };

  const del = async (n) => {
    if (!window.confirm("Delete this note?")) return;
    await api.deleteNote(n.id);
    toast.success("Note deleted");
    load();
  };

  return (
    <div data-testid="page-notes">
      <PageHeader
        overline="Field Intelligence"
        title="Notes"
        subtitle="Quick observations from visits, calls and tastings — linked to leads, customers, samples or tasks."
        actions={
          <div className="flex gap-2">
            <ExportButton module="notes" />
            <Button onClick={openCreate} className="bg-[#E85D04] hover:bg-[#c94b03] text-white" data-testid="add-note-btn">
              <Plus size={16} className="mr-2" /> New Note
            </Button>
          </div>
        }
      />

      <div className="card-soft p-4 mb-5 flex gap-2 flex-wrap items-center">
        <Label className="text-xs uppercase tracking-wider text-[#1F4D3A] font-semibold mr-2">Filter</Label>
        {["all", "lead", "customer", "sample", "task"].map((t) => (
          <button
            key={t}
            onClick={() => setFilter(t)}
            data-testid={`filter-${t}`}
            className="text-xs uppercase tracking-wider px-3 py-1.5 rounded-full font-bold transition-colors"
            style={{
              backgroundColor: filter === t ? "#E85D04" : "#F7F1E8",
              color: filter === t ? "white" : "#1F4D3A",
            }}
          >
            {t}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <EmptyState
          icon={StickyNote}
          title="No notes yet"
          description="Notes can be attached to leads, customers, samples or tasks."
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map((n) => (
            <div key={n.id} className="card-soft card-soft-hover p-4 flex flex-col gap-2" data-testid={`note-${n.id}`}>
              <div className="flex items-center justify-between">
                <span
                  className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full font-bold"
                  style={{
                    backgroundColor: `${PARENT_COLORS[n.parent_type]}1f`,
                    color: PARENT_COLORS[n.parent_type],
                  }}
                >
                  {n.parent_type}
                </span>
                <button
                  onClick={() => del(n)}
                  className="text-[#6C757D] hover:text-[#E76F51] p-1"
                  data-testid={`delete-note-${n.id}`}
                  aria-label="delete note"
                >
                  <Trash2 size={15} />
                </button>
              </div>
              {n.parent_label && (
                <div className="text-xs font-semibold text-[#1F4D3A] truncate">{n.parent_label}</div>
              )}
              <p className="text-sm text-[#222] whitespace-pre-wrap">{n.content}</p>
              <div className="text-[10px] uppercase tracking-wider text-[#6C757D] mt-auto">
                {formatDateEU(n.created_at)}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-lg" data-testid="note-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">New Note</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Attached To">
              <Select
                value={form.parent_type}
                onValueChange={(v) => setForm({ ...form, parent_type: v, parent_id: null, parent_label: "" })}
              >
                <SelectTrigger data-testid="input-note-parent-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="lead">Lead</SelectItem>
                  <SelectItem value="customer">Customer</SelectItem>
                  <SelectItem value="sample">Sample</SelectItem>
                  <SelectItem value="task">Task</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Record">
              <Select
                value={form.parent_id || "__none"}
                onValueChange={(v) => {
                  if (v === "__none") {
                    setForm({ ...form, parent_id: null, parent_label: "" });
                  } else {
                    const opt = parentOptions.find((o) => o.id === v);
                    setForm({ ...form, parent_id: v, parent_label: opt?.label || "" });
                  }
                }}
              >
                <SelectTrigger data-testid="input-note-parent-id"><SelectValue placeholder="Pick…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">— None / general —</SelectItem>
                  {parentOptions.map((o) => (<SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Content" className="col-span-2">
              <Textarea rows={4} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })}
                placeholder="What happened, what was discussed, next steps…"
                data-testid="input-note-content" />
            </Field>
          </div>
          <DialogFooter className="mt-2">
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="cancel-note">Cancel</Button>
            <Button onClick={save} className="bg-[#E85D04] hover:bg-[#c94b03] text-white" data-testid="save-note">Add Note</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Field = ({ label, children, className = "" }) => (
  <div className={`flex flex-col gap-1.5 ${className}`}>
    <Label className="text-xs uppercase tracking-wider text-[#1F4D3A] font-semibold">{label}</Label>
    {children}
  </div>
);
